import { useRef, useCallback } from 'react';

/**
 * Custom hook to detect whether a touch event is a tap or a scroll gesture.
 * Prevents accidental button clicks when scrolling on touch devices.
 * 
 * @param onOpenChange - Callback to control dropdown open/close state
 * @returns Touch event handlers for onPointerDown, onTouchStart, onTouchMove, onTouchEnd, and onClick
 * 
 * @example
 * const touchHandlers = useTouchScrollDetection(setOpen);
 * <Button {...touchHandlers} />
 */
export const useTouchScrollDetection = (onOpenChange?: (open: boolean) => void) => {
	const touchStartRef = useRef<{ x: number; y: number } | null>(null);
	const wasScrollingRef = useRef(false);
	const pendingOpenRef = useRef(false);

	const handlePointerDown = useCallback((e: React.PointerEvent) => {
		// Only intercept touch events, let mouse work normally
		if (e.pointerType === 'touch') {
			e.preventDefault(); // Prevent dropdown from opening immediately
			pendingOpenRef.current = true; // Mark that we want to open on tap
		}
	}, []);

	const handleTouchStart = useCallback((e: React.TouchEvent) => {
		const touch = e.touches[0];
		touchStartRef.current = { x: touch.clientX, y: touch.clientY };
		wasScrollingRef.current = false;
	}, []);

	const handleTouchMove = useCallback((e: React.TouchEvent) => {
		if (!touchStartRef.current) return;
		
		const touch = e.touches[0];
		const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
		const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
		
		// If moved more than 10px, it's a scroll
		if (deltaX > 10 || deltaY > 10) {
			wasScrollingRef.current = true;
			pendingOpenRef.current = false; // Cancel pending open
			if (onOpenChange) {
				onOpenChange(false); // Close if somehow opened
			}
		}
	}, [onOpenChange]);

	const handleTouchEnd = useCallback(() => {
		// If we have a pending open and didn't scroll, open the dropdown
		if (pendingOpenRef.current && !wasScrollingRef.current && onOpenChange) {
			onOpenChange(true);
		}
		
		touchStartRef.current = null;
		pendingOpenRef.current = false;
		
		setTimeout(() => {
			wasScrollingRef.current = false;
		}, 100);
	}, [onOpenChange]);

	const handleClick = useCallback((e: React.MouseEvent) => {
		if (wasScrollingRef.current) {
			e.preventDefault();
			e.stopPropagation();
		}
	}, []);

	return {
		onPointerDown: handlePointerDown,
		onTouchStart: handleTouchStart,
		onTouchMove: handleTouchMove,
		onTouchEnd: handleTouchEnd,
		onClick: handleClick,
	};
};
